import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { signUp } from "../../services/authService";
import {
  formatBrazilianMobile,
  normalizeEmail,
  onlyDigits,
} from "../../utils/inputFormatters";
import styles from "../signup/SignUp.module.css";

const validationSchema = Yup.object({
  acceptedTerms: Yup.boolean().oneOf([true], "Leia e aceite os termos para participar."),
  username: Yup.string()
    .trim()
    .min(3, "O nome de usuário deve ter pelo menos 3 caracteres")
    .max(30, "O nome de usuário deve ter no máximo 30 caracteres")
    .matches(
      /^[A-Za-z0-9._]+$/,
      "Use somente letras, números, ponto ou sublinhado"
    )
    .required("Informe o nome de usuário"),
  email: Yup.string()
    .trim()
    .email("Informe um e-mail válido")
    .required("Informe o e-mail"),
  mobile: Yup.string()
    .test(
      "mobile-pt-br",
      "Informe um celular brasileiro válido com DDD",
      (value) => !value || /^[1-9]{2}9\d{8}$/.test(onlyDigits(value))
    )
    .required("Informe o celular"),
  password: Yup.string()
    .min(6, "A senha deve ter pelo menos 6 caracteres")
    .matches(/[A-Za-z]/, "A senha deve conter pelo menos uma letra")
    .matches(/\d/, "A senha deve conter pelo menos um número")
    .matches(
      /^[A-Za-z\d@$!%*?&]+$/,
      "A senha contém um caractere especial não permitido"
    )
    .required("Informe a senha"),
  fullName: Yup.string()
    .trim()
    .min(2, "Informe o nome completo")
    .max(100, "O nome deve ter no máximo 100 caracteres")
    .required("Informe o nome completo"),
});

function SignUp() {
  const navigate = useNavigate(); // To redirect after successful signup

  // Formik hook
  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      mobile: "",
      password: "",
      fullName: "",
      acceptedTerms: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await signUp(values);
        if (response.success) {
          navigate('/');
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Não foi possível criar a conta."
        );
      }
    },
  });

  return (
    <div className={styles.signUp}>
      <aside className={styles.welcomePanel} aria-hidden="true">
        <div className={styles.welcomeContent}>
          <span className={styles.welcomeBadge}>Comece hoje</span>
          <h2>Seu próximo momento começa aqui.</h2>
          <p>Crie seu perfil e fique mais perto das pessoas que importam.</p>
        </div>
      </aside>
      <div className={styles.signUpFormDescription}>
        <div className={styles.logoNameDetail}>
          <div className={styles.logo}>
            <img src="/logopreta.png" alt="weDress" />
          </div>
          <div className={styles.description}>
            <span>
              <h3>Crie sua conta</h3>
              <p>Preencha seus dados para fazer parte da comunidade.</p>
            </span>
          </div>
        </div>
        <div className={styles.signUpForm}>
          <form onSubmit={formik.handleSubmit}>
            <div className={styles.signUpFormContant}>
              <label htmlFor="email">
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="E-mail"
                  autoComplete="email"
                  required
                  onChange={formik.handleChange}
                  onBlur={(event) => {
                    formik.setFieldValue(
                      "email",
                      normalizeEmail(event.target.value)
                    );
                    formik.handleBlur(event);
                  }}
                  value={formik.values.email}
                  className={
                    formik.touched.email && formik.errors.email ? "error" : ""
                  }
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="error-message">{formik.errors.email}</div>
                )}
              </label>

              <label htmlFor="mobile">
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  placeholder="Celular (DDD) 99999-9999"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={15}
                  required
                  onChange={(event) =>
                    formik.setFieldValue(
                      "mobile",
                      formatBrazilianMobile(event.target.value)
                    )
                  }
                  onBlur={formik.handleBlur}
                  value={formik.values.mobile}
                  className={
                    formik.touched.mobile && formik.errors.mobile ? "error" : ""
                  }
                />
                {formik.touched.mobile && formik.errors.mobile && (
                  <div className="error-message">{formik.errors.mobile}</div>
                )}
              </label>

              <label htmlFor="password">
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Senha"
                  autoComplete="new-password"
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  className={
                    formik.touched.password && formik.errors.password
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.password && formik.errors.password && (
                  <div className="error-message">{formik.errors.password}</div>
                )}
              </label>

              <label htmlFor="fullName">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Nome completo"
                  autoComplete="name"
                  maxLength={100}
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.fullName}
                  className={
                    formik.touched.fullName && formik.errors.fullName
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.fullName && formik.errors.fullName && (
                  <div className="error-message">{formik.errors.fullName}</div>
                )}
              </label>

              <label htmlFor="username">
                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Nome de usuário"
                  autoComplete="username"
                  maxLength={30}
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.username}
                  className={
                    formik.touched.username && formik.errors.username
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.username && formik.errors.username && (
                  <div className="error-message">{formik.errors.username}</div>
                )}
              </label>
            </div>

            <div className={styles.tncDes}>
              <p>Projeto escolar para uma feira. Seus dados serão usados somente para essa finalidade e apagados após seu encerramento, sem reutilização futura pela equipe.</p>
              <label className={styles.termsConsent} htmlFor="acceptedTerms">
                <input type="checkbox" id="acceptedTerms" name="acceptedTerms" checked={formik.values.acceptedTerms} onChange={formik.handleChange} onBlur={formik.handleBlur} required aria-invalid={Boolean(formik.touched.acceptedTerms && formik.errors.acceptedTerms)} aria-describedby="terms-consent-error" />
                <span>Li e aceito os <Link to="/termos" target="_blank" rel="noopener noreferrer">termos de uso e a política de privacidade</Link>.</span>
              </label>
              {formik.touched.acceptedTerms && formik.errors.acceptedTerms && <p id="terms-consent-error" className="error-message">{formik.errors.acceptedTerms}</p>}
            </div>
            <div className={styles.submitButton}>
              <button type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? "Criando conta..." : "Criar conta"}
              </button>
            </div>
          </form>
        </div>
        <div className={styles.signInRedirect}>
          <p>
            Já tem uma conta? <Link to="/">Entrar</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
